import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Phone, FileText, Mail, Clock, CheckCircle, Plus, Minus } from 'lucide-react';
interface LegalHelpProps {
  onNavigate?: (path: string) => void;
}
interface FAQItemProps {
  question: string;
  answer: React.ReactNode;
  isOpen: boolean;
  onToggle: () => void;
}
function FAQItem({
  question,
  answer,
  isOpen,
  onToggle
}: FAQItemProps) {
  return <div className={`bg-white border border-borderColor rounded-xl overflow-hidden transition-all duration-200 ${isOpen ? 'shadow-md' : 'shadow-sm'}`} data-id="element-2789">
      <button onClick={onToggle} className="w-full flex items-center justify-between p-6 text-left focus:outline-none" data-id="element-2790">
        <span className={`text-lg font-medium ${isOpen ? 'text-violetBlue' : 'text-primary'}`} data-id="element-2791">
          {question}
        </span>
        <div className={`p-1 rounded-full transition-colors ${isOpen ? 'bg-aliceBlue text-violetBlue' : 'text-secondary'}`} data-id="element-2792">
          {isOpen ? <Minus className="w-5 h-5" data-id="element-2793" /> : <Plus className="w-5 h-5" data-id="element-2794" />}
        </div>
      </button>

      {isOpen && <div className="px-6 pb-6 pt-0" data-id="element-2795">
          <div className="border-t border-borderColor pt-4 text-secondary text-sm leading-relaxed" data-id="element-2796">
            {answer}
          </div>
        </div>}
    </div>;
}
export function LegalHelp({
  onNavigate
}: LegalHelpProps) {
  const navigate = useNavigate();
  const [openFaqIndex, setOpenFaqIndex] = useState<number | null>(0);
  const handleCallNow = () => {
    window.location.href = 'tel:1-877-213-4543';
  };
  const handleFillOutForm = () => {
    // Navigate to intake form or open modal
    console.log('Opening intake form');
  };
  const handleSendEmail = () => {
    window.location.href = 'mailto:legal@lawvo.com';
  };
  const handleToggleFaq = (index: number) => {
    setOpenFaqIndex(openFaqIndex === index ? null : index);
  };
  return <div className="min-h-screen bg-white" data-id="element-2797">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-8" data-id="element-2798">
        {/* Header */}
        <div className="mb-6 sm:mb-8" data-id="element-2799">
          <h2 className="text-2xl sm:text-3xl font-semibold text-primary mb-2" data-id="element-2800">
            24/7 Legal Help
          </h2>
          <p className="text-sm sm:text-base text-secondary max-w-4xl" data-id="element-2801">
            Access legal advice and support through multiple channels. Choose
            the method that best suits your needs.
          </p>
        </div>

        {/* Contact Methods Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6 mb-6 sm:mb-8" data-id="element-2802">
          {/* 24/7 Call Helpline */}
          <div className="bg-white border border-borderColor rounded-xl p-6 shadow-sm flex flex-col" data-id="element-2803">
            <div className="flex items-start gap-4 mb-4" data-id="element-2804">
              <div className="p-3 bg-aliceBlue rounded-lg flex-shrink-0" data-id="element-2805">
                <Phone className="w-6 h-6 text-violetBlue" data-id="element-2806" />
              </div>
              <h3 className="text-xl font-semibold text-primary leading-tight pt-1" data-id="element-2807">
                24/7 Call Helpline
              </h3>
            </div>
            <p className="text-sm text-secondary mb-5 leading-relaxed flex-grow" data-id="element-2808">
              Speak with a qualified lawyer immediately for urgent legal matters
            </p>
            <div className="mb-5" data-id="element-2809">
              <p className="text-xl font-bold text-primary mb-1" data-id="element-2810">
                1-877-213-4543
              </p>
              <p className="text-sm text-secondary" data-id="element-2811">Available 24/7</p>
            </div>
            <button onClick={handleCallNow} className="w-full bg-white border-2 border-violetBlue text-violetBlue hover:bg-aliceBlue font-medium py-2.5 px-4 rounded-lg transition-colors mt-auto" data-id="element-2812">
              Request a Call
            </button>
          </div>

          {/* Direct Email Access */}
          <div className="bg-white border border-borderColor rounded-xl p-6 shadow-sm flex flex-col" data-id="element-2813">
            <div className="flex items-start gap-4 mb-4" data-id="element-2814">
              <div className="p-3 bg-aliceBlue rounded-lg flex-shrink-0" data-id="element-2815">
                <Mail className="w-6 h-6 text-violetBlue" data-id="element-2816" />
              </div>
              <h3 className="text-xl font-semibold text-primary leading-tight pt-1" data-id="element-2817">
                Direct Email Access
              </h3>
            </div>
            <p className="text-sm text-secondary mb-5 leading-relaxed flex-grow" data-id="element-2818">
              Contact your assigned lawyer directly for non-urgent matters
            </p>
            <div className="mb-5" data-id="element-2819">
              <p className="text-base font-bold text-primary mb-1" data-id="element-2820">
                legal@lawvo.com
              </p>
              <p className="text-sm text-secondary" data-id="element-2821">
                Response within 24 business hours
              </p>
            </div>
            <button onClick={handleSendEmail} className="w-full bg-white border-2 border-violetBlue text-violetBlue hover:bg-aliceBlue font-medium py-2.5 px-4 rounded-lg transition-colors mt-auto" data-id="element-2822">
              Send Email
            </button>
          </div>
        </div>

        {/* How to Choose Section */}

        {/* FAQ Section */}
        <div className="space-y-4" data-id="element-2823">
          <FAQItem question="What is a 24/7 Legal Helpline?" isOpen={openFaqIndex === 0} onToggle={() => handleToggleFaq(0)} answer="A 24/7 legal helpline is a service provided by LawVo that offers clients access to legal advice and support at any time, day or night. This service is designed to provide clients with immediate assistance and guidance in the event of a legal issue or emergency, without the need to wait for regular business hours or book an appointment with a lawyer." data-id="element-2824" />

          <FAQItem question="How Does LawVo's 24/7 Legal Helpline Work?" isOpen={openFaqIndex === 1} onToggle={() => handleToggleFaq(1)} answer={<div className="space-y-4" data-id="element-2826">
                <p data-id="element-2827">
                  Simply call{' '}
                  <span className="text-turquoise font-bold" data-id="element-2828">
                    1-833-URLAWVO
                  </span>{' '}
                  to access legal advice and support at any time. When a client
                  contacts the helpline, they will be connected with a qualified
                  lawyer or legal professional who can provide guidance on their
                  legal issue or concern.
                </p>
                <p data-id="element-2829">
                  The lawyer or legal professional may ask the client to provide
                  details about their legal issue, such as the nature of the
                  problem, any relevant documents or contracts, and the current
                  status of the situation. They may also offer advice and
                  support on how to best resolve the issue, including legal
                  options and next steps.
                </p>
              </div>} data-id="element-2825" />
        </div>
      </div>
    </div>;
}